import { useRef, type CSSProperties } from 'react';
import '../components/atomic/layout/app_screen/app_screen.css';
import './map_screen.css';
import {
  formatSignedRatingDelta,
  QUIZ_QUESTIONS,
  roundRating,
  shouldShowDailyPackCompleteModal,
} from '../../../domain';
import { isAdminClient, openExternalLink } from '../../../infrastructure';
import { useAppStore } from '../../store/appStore';
import { Arrows } from '../components/atomic/buttons/arrows/Arrows';
import { TicketsScreenButton } from './tickets_screen_button/TicketsScreenButton';
import { RatingScreenButton } from './rating_screen_button/RatingScreenButton';
import { PlayerLevelScreenButton } from './player_level_screen_button/PlayerLevelScreenButton';
import { NavBar } from '../components/composite/nav/nav_bar/NavBar';
import { MuteToggleButton } from '../pause_screen/mute_toggle/MuteToggleButton';
import { playUiClick } from '../../assets/preloadSounds';
import { Lands } from './lands/Lands';
import { Giants } from './giants/Giants';
import { MapDeliveryPopups } from './delivery_popups/MapDeliveryPopups';
import { MapDeliveryHints } from './delivery_hint_icon/MapDeliveryHints';
import { AllDeliveryCompletedModal } from './all_delivery_completed/AllDeliveryCompletedModal';
import {
  MAP_SCREEN_WORLD,
  MAP_ZOOM_MAX_SCALE,
  MAP_ZOOM_MIN_SCALE,
  mapArrowNudge,
  mapHudElementScale,
  mapWorldTransform,
  type MapScreenModel,
} from './MapScreenModelScreen';
import type { MapScreenController } from './MapScreenController';
import type { ScreenViewProps } from '../shared/screenViewProps';
import { useMapPan } from '../shared/gestures/useMapPan';

const ADMIN_QUIZ_HINT_URL =
  QUIZ_QUESTIONS.find((question) => question.rutubeUrl)?.rutubeUrl ?? '';

export type MapScreenViewProps = ScreenViewProps<
  MapScreenModel,
  MapScreenController
>;

function formatHudRating(averageRating: number | undefined): string | undefined {
  if (averageRating == null || !Number.isFinite(averageRating)) {
    return undefined;
  }
  return roundRating(averageRating).toFixed(1).replace('.', ',');
}

function formatHudRatingDelta(delta: number | null | undefined): string {
  if (delta == null || !Number.isFinite(delta)) {
    return '';
  }
  return formatSignedRatingDelta(delta).replace('.', ',');
}

export function MapScreenView({ model, controller }: MapScreenViewProps) {
  const viewportRef = useRef<HTMLDivElement>(null);
  const showCompleteModal = useAppStore((s) =>
    shouldShowDailyPackCompleteModal(s.playerEconomy),
  );
  const resetPlayerProgressForAdmin = useAppStore((s) => s.resetPlayerProgressForAdmin);
  const grantPassesForAdmin = useAppStore((s) => s.grantPassesForAdmin);
  const audioLevel = useAppStore((s) => s.audioLevel);
  const cycleAudioLevel = useAppStore((s) => s.cycleAudioLevel);
  const pan = useMapPan({
    getCamera: () => model.camera,
    onPan: controller.handlePan,
    enabled: !showCompleteModal,
    minScale: MAP_ZOOM_MIN_SCALE,
    maxScale: MAP_ZOOM_MAX_SCALE,
    viewportRef,
  });

  const handleAdminReset = () => {
    if (!window.confirm('Сбросить прогресс доставок?')) {
      return;
    }
    void resetPlayerProgressForAdmin();
  };

  const handleAdminQuizHint = () => {
    void openExternalLink(ADMIN_QUIZ_HINT_URL);
  };

  const handleAdminGrantPasses = () => {
    void grantPassesForAdmin();
  };

  const hudScale = mapHudElementScale(model.fitScale);

  return (
    <div
      ref={viewportRef}
      className="app-screen map-screen"
      style={
        {
          ...pan.style,
          '--map-hud-inv-x': String(hudScale.invX),
          '--map-hud-inv-y': String(hudScale.invY),
          '--map-hud-uniform': String(hudScale.uniform),
        } as CSSProperties
      }
      onPointerDown={pan.onPointerDown}
      onPointerMove={pan.onPointerMove}
      onPointerUp={pan.onPointerUp}
      onPointerCancel={pan.onPointerCancel}
    >
      <div
        className={[
          'map-screen-world',
          pan.isDragging ? 'map-screen-world--dragging' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        style={{
          width: MAP_SCREEN_WORLD.width,
          height: MAP_SCREEN_WORLD.height,
          transform: mapWorldTransform(model.camera, model.fitScale),
          pointerEvents: showCompleteModal ? 'none' : undefined,
        }}
      >
        <Lands />
        <Giants />
        <MapDeliveryPopups onTakeDelivery={controller.handleTakeDelivery} />
      </div>

      <Arrows
        directions={['left', 'right']}
        positions={{
          left: { left: 48.4248, top: 0 },
          right: { left: 938.657, top: 0 },
        }}
        onNavigate={(direction) => {
          pan.nudgeBy(mapArrowNudge(model.camera, direction, model.viewSize));
        }}
        canNavigate={
          showCompleteModal
            ? { left: false, right: false }
            : model.canNavigate
        }
      >
        {showCompleteModal ? null : (
          <MapDeliveryHints
            camera={model.camera}
            directions={['left', 'right']}
            viewportWidth={model.viewSize.width}
            viewportHeight={model.viewSize.height}
          />
        )}
      </Arrows>

      <div className="map-screen-hud-top-bar">
        <RatingScreenButton
          onClick={controller.handleOpenRating}
          rating={formatHudRating(model.averageRating)}
          changeCoeff={formatHudRatingDelta(model.lastRatingDelta)}
        />
        <PlayerLevelScreenButton
          onClick={controller.handleOpenPlayerLevel}
          name={model.playerLevelName}
        />
        {isAdminClient() ? (
          <div className="map-screen__admin-actions">
            <button
              type="button"
              className="map-screen__admin-reset"
              onClick={handleAdminReset}
            >
              Сброс
            </button>
            <button
              type="button"
              className="map-screen__admin-reset"
              onClick={handleAdminQuizHint}
            >
              Подсказка квиза
            </button>
            <button
              type="button"
              className="map-screen__admin-reset"
              onClick={handleAdminGrantPasses}
            >
              +5 билетов
            </button>
          </div>
        ) : null}
        <TicketsScreenButton
          onClick={controller.handleOpenTickets}
          count={model.passesBalance}
        />
        <MuteToggleButton
          variant="map"
          level={audioLevel}
          onClick={() => {
            playUiClick();
            void cycleAudioLevel();
          }}
        />
      </div>

      {showCompleteModal ? <AllDeliveryCompletedModal /> : null}

      <NavBar
        activeTab={model.activeTab}
        onTabChange={controller.handleTabChange}
      />
    </div>
  );
}
